// @shared:standard — helper de données extrait de TaskTypeConfigSection.jsx.
//
// Il vit dans un module .js DÉDIÉ, et non à côté du composant : un export
// non-composant depuis un `.jsx` casse le Fast Refresh (la zone concernée
// disparaît de l'écran, sans trace au build), et ce helper est aussi importé
// par `services/projectSettings.js` — un service ne doit pas tirer React,
// `lucide-react` et le DS derrière lui pour une fonction de données.

/**
 * Build a hierarchical map of task type names → subtypes and metadata
 * from the raw backend collection items (same shape as backendTaskTypes in
 * ConfigurationPage / taskTypes in KanbanBoard).
 *
 * Top-level terms (parentId === 0) become keys of the map.
 * Child terms (parentId > 0) are grouped under their parent's entry.
 *
 * Each entry in `meta` carries: tid, workflowId, workflowStates, workflowTransitions.
 * Subtypes have the same meta shape so they can also be assigned workflows.
 *
 * Exported so callers can pre-build the maps when they also need them for other
 * purposes (e.g. the task-rights matrix in ProjectSettingsModal).
 */
export function buildTaskTypesMap(taskTypes) {
  if (!taskTypes?.length) return { map: {}, meta: {}, subtypeMeta: {} };

  const termMeta = {};
  taskTypes.forEach(tt => {
    const id = tt?.data?.id;
    if (!id) return;
    termMeta[id] = {
      tid: id,
      name: tt?.data?.name || tt?.label || '',
      parentId: tt?.data?.parentId ?? 0,
      workflowId: tt?.data?.workflow?.workflowId ?? null,
      workflowStates: tt?.data?.workflow?.workflowStates ?? null,
      workflowTransitions: tt?.data?.workflow?.workflowTransitions ?? null,
    };
  });

  const map = {};
  const meta = {};
  const subtypeMeta = {};

  // First pass: register top-level types.
  Object.values(termMeta).forEach(term => {
    if (term.parentId !== 0) return;
    map[term.name] = [];
    meta[term.name] = {
      tid: term.tid,
      workflowId: term.workflowId,
      workflowStates: term.workflowStates,
      workflowTransitions: term.workflowTransitions,
    };
  });

  // Second pass: attach subtypes to their parent type.
  Object.values(termMeta).forEach(term => {
    if (term.parentId === 0) return;
    const parent = termMeta[term.parentId];
    if (!parent || !map[parent.name]) return;
    map[parent.name].push(term.name);
    subtypeMeta[term.name] = {
      tid: term.tid,
      parentTid: term.parentId,
      workflowId: term.workflowId,
      workflowStates: term.workflowStates,
      workflowTransitions: term.workflowTransitions,
    };
  });

  return { map, meta, subtypeMeta };
}
